/**
 * 🏛️ SellToHouse — Auction House vendor buy-back tab.
 *
 * Users pick any owned gear piece or consumable and the Auctioneer buys
 * it back for a fair chit price (per-rarity ratio × House catalog price).
 * The toggle is owner-controlled and defaults to ON.
 *
 * Endpoints:
 *   GET  /api/auction-house/buyback/sellable   → { gear, consumables, config }
 *   POST /api/auction-house/buyback/sell       { kind, item_id, qty }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_BORDER = {
  common:    "border-neutral-600",
  uncommon:  "border-emerald-700",
  rare:      "border-blue-700",
  epic:      "border-purple-700",
  legendary: "border-amber-600",
  mythic:    "border-fuchsia-600",
  cosmic:    "border-cyan-500",
};

export default function SellToHouse() {
  const [data, setData] = useState({ gear: [], consumables: [], config: null });
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState({});

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/auction-house/buyback/sellable`);
      setData(r.data || { gear: [], consumables: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Buy-back list failed to load.");
    }
  };

  useEffect(() => { refresh(); }, []);

  const setItemQty = (key, val) =>
    setQty((q) => ({ ...q, [key]: Math.max(1, parseInt(val) || 1) }));

  const sell = async (kind, item_id, ownedQty, itemName, unitChits) => {
    const key = `${kind}:${item_id}`;
    const n = Math.max(1, Math.min(ownedQty, parseInt(qty[key] || 1)));
    // 🛡️ Feb 2026 owner spec (B39): SELL is a one-click destructive action.
    // Always confirm before dispatching so accidental clicks can't burn
    // gear.
    const ok = await confirmDialog({
      title: `Sell ${n}× ${itemName || "item"}?`,
      message: `You'll receive ${(Number(unitChits || 0) * n).toLocaleString()} 🪙 Chits from the Auctioneer. This can't be undone.`,
      confirmLabel: "Sell",
      cancelLabel: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/buyback/sell`, {
        kind, item_id, qty: n,
      });
      toast.success(`💰 +${r.data.chits_paid.toLocaleString()} 🪙 for ${n}× ${r.data.name}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sell failed.");
    }
    setBusy(false);
  };

  const cfg = data.config;
  const disabled = cfg && !cfg.enabled;

  if (disabled) {
    return (
      <div
        data-testid="buyback-disabled"
        className="rounded-2xl border border-red-700/40 bg-red-950/20 p-6 text-center"
      >
        <div className="text-3xl mb-2">🚫</div>
        <div className="text-lg font-black text-red-200">Buy-Back is currently closed.</div>
        <div className="text-xs text-red-300/70 mt-1">
          The Auctioneer isn&apos;t buying items right now. Check back later.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sell-to-house" className="space-y-5">
      <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a1000] via-black/40 to-[#20180b] p-4">
        <div className="text-2xl font-black tracking-wide text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
          🏛️ SELL TO THE AUCTIONEER
        </div>
        <div className="text-[11px] text-neutral-400 mt-1 max-w-2xl">
          Instant vendor buy-back for gear and consumables you no longer need. Paid in 🪙 Chits at the current per-rarity rate.
          {cfg?.ratios && (
            // Roc #23 (Sep 2026) — rate line was mis-rendering as "Common 50 %
            // · Uncommon % · Uncommon 50 % …" because the segments wrapped
            // mid-rarity. Map-based render keeps each segment on one line and
            // guarantees exactly 5 unique rarities, in order, each with its
            // own percentage (never duplicated, never missing a value).
            <span
              className="ml-2 opacity-75 inline-flex flex-wrap gap-x-2 gap-y-1 items-center align-middle"
              data-testid="sell-rate-line"
            >
              {[
                ["common",    "Common"],
                ["uncommon",  "Uncommon"],
                ["rare",      "Rare"],
                ["epic",      "Epic"],
                ["legendary", "Legendary"],
              ].map(([key, label], idx) => {
                const raw = cfg.ratios[key];
                const pct = Math.round((Number.isFinite(raw) ? raw : 0.5) * 100);
                return (
                  <span key={key} className="whitespace-nowrap" data-testid={`sell-rate-${key}`}>
                    {label} {pct}%{idx < 4 ? " ·" : ""}
                  </span>
                );
              })}
            </span>
          )}
        </div>
      </div>

      {/* Gear */}
      <section data-testid="sell-gear-section">
        <div className="text-sm font-black tracking-wider text-amber-200/90 mb-2">
          ⚔️ GEAR & WEAPONS ({data.gear.length})
        </div>
        {data.gear.length === 0 ? (
          <div className="rounded-xl border border-neutral-800 bg-black/30 p-6 text-center text-xs text-neutral-500">
            No sellable gear in your inventory. Soulbound pieces don&apos;t qualify.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.gear.map((it) => (
              <SellCard
                key={`gear-${it.item_id}`}
                kind="gear"
                item={it}
                qty={qty[`gear:${it.item_id}`] || 1}
                onQty={(v) => setItemQty(`gear:${it.item_id}`, v)}
                onSell={() => sell("gear", it.item_id, it.owned_qty, it.name, it.unit_chits)}
                busy={busy}
              />
            ))}
          </div>
        )}
      </section>

      {/* Consumables */}
      <section data-testid="sell-consumables-section">
        <div className="text-sm font-black tracking-wider text-amber-200/90 mb-2">
          🧪 CONSUMABLES ({data.consumables.length})
        </div>
        {data.consumables.length === 0 ? (
          <div className="rounded-xl border border-neutral-800 bg-black/30 p-6 text-center text-xs text-neutral-500">
            No sellable consumables in your inventory.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.consumables.map((it) => (
              <SellCard
                key={`con-${it.item_id}`}
                kind="consumable"
                item={it}
                qty={qty[`consumable:${it.item_id}`] || 1}
                onQty={(v) => setItemQty(`consumable:${it.item_id}`, v)}
                onSell={() => sell("consumable", it.item_id, it.owned_qty, it.name, it.unit_chits)}
                busy={busy}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SellCard({ kind, item, qty, onQty, onSell, busy }) {
  const border = RARITY_BORDER[item.rarity] || "border-neutral-700";
  const clamped = Math.max(1, Math.min(item.owned_qty || 1, qty));
  const total = (item.unit_price || 0) * clamped;
  return (
    <div
      className={`rounded-xl border ${border} bg-black/50 p-3 flex flex-col gap-2`}
      data-testid={`sell-card-${kind}-${item.item_id}`}
    >
      <div className="flex items-start gap-2">
        <div className="text-3xl leading-none">{item.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-amber-100 leading-tight truncate">{item.name}</div>
          <div className="text-[10px] text-neutral-400 uppercase tracking-wider mt-0.5">
            {item.rarity} · owned {item.owned_qty}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onQty(clamped - 1)}
          disabled={busy || clamped <= 1}
          className="w-6 h-6 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-amber-200 font-bold text-xs disabled:opacity-40"
          data-testid={`sell-qty-dec-${kind}-${item.item_id}`}
        >−</button>
        <input
          type="number"
          min="1"
          max={item.owned_qty}
          value={clamped}
          onChange={(e) => onQty(e.target.value)}
          className="w-14 px-2 py-1 rounded bg-black/70 border border-neutral-600 text-center text-amber-100 text-xs"
          data-testid={`sell-qty-input-${kind}-${item.item_id}`}
        />
        <button
          onClick={() => onQty(clamped + 1)}
          disabled={busy || clamped >= item.owned_qty}
          className="w-6 h-6 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-amber-200 font-bold text-xs disabled:opacity-40"
          data-testid={`sell-qty-inc-${kind}-${item.item_id}`}
        >+</button>
        <div className="ml-auto text-[11px] text-neutral-400">
          @ 🪙 {item.unit_price.toLocaleString()}
        </div>
      </div>

      <button
        onClick={onSell}
        disabled={busy || total <= 0}
        data-testid={`sell-btn-${kind}-${item.item_id}`}
        className="px-3 py-2 rounded-lg text-xs font-black border transition-all bg-amber-600/80 hover:bg-amber-500 border-amber-400/50 text-black shadow-[0_0_18px_rgba(251,191,36,0.25)] disabled:bg-neutral-800/60 disabled:border-neutral-700 disabled:text-neutral-500"
      >
        💰 SELL FOR 🪙 {total.toLocaleString()}
      </button>
    </div>
  );
}
